import type { HTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';
import { useInternalStatus, type ValidationStatus } from '@/hooks/useInternalStatus';
import { FormLabel, type FormLabelProps } from './FormLabel';
import { Icon } from '@/atoms/Icons';
import classnames from 'classnames';

export type FormControlProps<
  Value = unknown,
  Attributes extends HTMLAttributes<HTMLElement> = InputHTMLAttributes<HTMLInputElement>,
> = {
  value?: Value;
  label?: ReactNode;
  validationStatus?: ValidationStatus;
  onChange?: (name: string, value: Value) => void;
  dataTestId?: string;
  dataName?: string;
  errorMessage?: string;
} & Omit<Attributes & Partial<FormLabelProps>, 'onChange' | 'value'>;

export const FormControl = <Value = string,>({
  id,
  label,
  description,
  disabled,
  required,
  className,
  validationStatus = 'default',
  children,
  errorMessage,
  dataTestId,
  dataName,
  hideRequiredStar,
  layout,
}: FormControlProps<Value>) => {
  const internalStatus = useInternalStatus({
    isDisabled: !!disabled,
    validationStatus,
  });

  const shouldDisplayErrorMessage = internalStatus === 'error' && errorMessage;

  return (
    <div
      className={classnames('flex flex-col gap-4', className)}
      data-name={dataName}
      data-testid={dataTestId}
    >
      {label && id && (
        <FormLabel
          description={description}
          id={id}
          layout={layout}
          required={required}
          hideRequiredStar={hideRequiredStar}
        >
          {label}
        </FormLabel>
      )}

      {children}

      {shouldDisplayErrorMessage && (
        <span className="text-red text-b4 flex items-start gap-x-4 px-20">
          <Icon name="Error" width="20px" />
          {errorMessage}
        </span>
      )}
    </div>
  );
};
